import { useRef, useCallback, useState as useReactState } from "react";
import {
  ShieldAlert,
  Plane,
  Pill,
  Calendar,
  FileDown,
  ClipboardList,
  AlertTriangle,
  Syringe,
  Globe,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type {
  TravelHealthPlan,
  TravelHealthVisit,
  TravelHealthDoseAuditRecord,
  TravelHealthPlanProduct,
  CoAdminConstraint,
  SafetyFlagAck,
} from "@/lib/travelHealth/types";

export interface TravelHealthSummaryData {
  _type: "travel-health-consultation";
  plan: TravelHealthPlan;
  visits: TravelHealthVisit[];
  doseAuditRecords: TravelHealthDoseAuditRecord[];
}

interface TravelHealthConsultationSummaryProps {
  isDarkMode?: boolean;
  data: TravelHealthSummaryData;
}

const TRIP_TYPE_LABELS: Record<string, string> = {
  holiday: "Holiday",
  "business-trip": "Business Trip",
  backpacking: "Backpacking",
  "hotel-stay": "Hotel Stay",
  cruise: "Cruise",
  "camping-hostels": "Camping / Hostels",
  "living-abroad": "Living Abroad",
  safari: "Safari",
  "adventure-travel": "Adventure Travel",
  "volunteer-relief-work": "Volunteer / Relief Work",
  "hajj-umrah": "Hajj / Umrah",
  diving: "Diving",
  "healthcare-worker": "Healthcare Worker",
  "medical-dental-treatment-abroad": "Medical / Dental Treatment Abroad",
  "visiting-friends-relatives": "Visiting Friends & Relatives",
};

const ACTIVITY_LABELS: Record<string, string> = {
  "trekking-high-altitude": "Trekking / High Altitude",
  "freshwater-contact": "Freshwater Contact",
  "animal-contact": "Animal Contact",
  "outdoor-remote-areas": "Outdoor / Remote Areas",
  "camping-sleeping-outdoors": "Camping / Sleeping Outdoors",
  "outside-dawn-dusk-overnight": "Outside at Dawn / Dusk / Overnight",
  "healthcare-settings": "Healthcare Settings",
  "relief-aid-work": "Relief / Aid Work",
  "farming-animals": "Farming / Animals",
  "caves-bat-exposure": "Caves / Bat Exposure",
  "mass-gathering": "Mass Gathering",
  "rural-remote-far-from-care": "Rural / Remote / Far from Care",
  "long-stay-over-one-month": "Long Stay (>1 month)",
  "adventure-extreme-sports": "Adventure / Extreme Sports",
  "tattoos-piercings-cosmetic-abroad": "Tattoos / Piercings / Cosmetic Abroad",
  "new-sexual-partner": "New Sexual Partner",
  "long-haul-5plus-timezones": "Long Haul (5+ Timezones)",
};

const SAFETY_FLAG_LABELS: Record<string, string> = {
  "pregnant-or-breastfeeding": "Pregnant or Breastfeeding",
  immunosuppressed: "Immunosuppressed",
  asplenia: "Asplenia",
  "hiv-positive": "HIV Positive",
  "yellow-fever-required": "Yellow Fever Required",
  "hajj-umrah": "Hajj / Umrah",
};

const PRODUCT_STATUS_CONFIG: Record<
  string,
  { label: string; light: string; dark: string }
> = {
  planned: {
    label: "Planned",
    light: "bg-blue-50 border-blue-300 text-blue-700",
    dark: "bg-blue-900/20 border-blue-700 text-blue-300",
  },
  "in-progress": {
    label: "In Progress",
    light: "bg-amber-50 border-amber-300 text-amber-700",
    dark: "bg-amber-900/20 border-amber-700 text-amber-300",
  },
  complete: {
    label: "Complete",
    light: "bg-green-50 border-green-300 text-green-700",
    dark: "bg-green-900/20 border-green-700 text-green-300",
  },
  declined: {
    label: "Declined",
    light: "bg-gray-50 border-gray-300 text-gray-700",
    dark: "bg-gray-900/20 border-gray-700 text-gray-300",
  },
  referred: {
    label: "Referred",
    light: "bg-purple-50 border-purple-300 text-purple-700",
    dark: "bg-purple-900/20 border-purple-700 text-purple-300",
  },
};

const PLAN_STATUS_CONFIG: Record<
  string,
  { label: string; light: string; dark: string }
> = {
  active: {
    label: "Active",
    light: "bg-blue-50 border-blue-300 text-blue-700",
    dark: "bg-blue-900/20 border-blue-700 text-blue-300",
  },
  complete: {
    label: "Complete",
    light: "bg-green-50 border-green-300 text-green-700",
    dark: "bg-green-900/20 border-green-700 text-green-300",
  },
  abandoned: {
    label: "Abandoned",
    light: "bg-red-50 border-red-300 text-red-700",
    dark: "bg-red-900/20 border-red-700 text-red-300",
  },
};

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return "Not specified";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(dateStr: string | undefined): string {
  if (!dateStr) return "Not specified";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function TravelHealthConsultationSummary({
  isDarkMode = false,
  data,
}: TravelHealthConsultationSummaryProps) {
  const { plan, visits, doseAuditRecords } = data;

  const sectionCard = `rounded-2xl border p-4 space-y-3 transition-colors duration-300 ${
    isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
  }`;
  const sectionHeading = `text-sm font-semibold flex items-center gap-2 ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;
  const rowLabel = `text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`;
  const rowValue = `text-sm font-medium ${
    isDarkMode ? "text-gray-100" : "text-gray-900"
  }`;
  const divider = `border-t ${
    isDarkMode ? "border-gray-700" : "border-gray-100"
  }`;

  const Row = ({
    label,
    value,
  }: {
    label: string;
    value: string | React.ReactNode;
  }) => (
    <div className="flex flex-col gap-0.5">
      <span className={rowLabel}>{label}</span>
      <span className={rowValue}>{value}</span>
    </div>
  );

  const StatusBadge = ({
    status,
    config,
  }: {
    status: string;
    config: Record<string, { label: string; light: string; dark: string }>;
  }) => {
    const cfg = config[status] || {
      label: status,
      light: "bg-gray-50 border-gray-300 text-gray-700",
      dark: "bg-gray-900/20 border-gray-700 text-gray-300",
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
          isDarkMode ? cfg.dark : cfg.light
        }`}
      >
        {cfg.label}
      </span>
    );
  };

  const completedProducts = plan.basket.filter(
    (p) => p.status === "complete",
  ).length;
  const totalProducts = plan.basket.length;
  const planAuditRecords = doseAuditRecords.filter(
    (r) => r.planId === plan.id,
  );
  const planVisits = visits.filter((v) => v.planId === plan.id);

  const summaryRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useReactState(false);

  const handleDownloadPdf = useCallback(async () => {
    if (!summaryRef.current || downloading) return;
    setDownloading(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(summaryRef.current, {
        scale: 1.25,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const pageHeightPx = Math.floor((canvasWidth * pageHeight) / pageWidth);

      let renderedHeight = 0;
      let pageIndex = 0;

      while (renderedHeight < canvasHeight) {
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvasWidth;
        pageCanvas.height = Math.min(pageHeightPx, canvasHeight - renderedHeight);

        const ctx = pageCanvas.getContext("2d")!;
        ctx.drawImage(
          canvas,
          0, renderedHeight, canvasWidth, pageCanvas.height,
          0, 0, canvasWidth, pageCanvas.height,
        );

        const imgData = pageCanvas.toDataURL("image/jpeg", 0.72);
        if (pageIndex > 0) pdf.addPage();

        const imgWidth = pageWidth - 20;
        const imgHeight = (pageCanvas.height * imgWidth) / pageCanvas.width;
        pdf.addImage(imgData, "JPEG", 10, 10, imgWidth, imgHeight);

        renderedHeight += pageCanvas.height;
        pageIndex++;
      }

      const dest = (plan.tripDestinations?.[0] || "summary").replace(/[^a-zA-Z0-9]/g, "_");
      const dateStr = new Date().toISOString().split("T")[0];
      pdf.save(`TravelHealth_${dest}_${dateStr}.pdf`);
    } catch (err) {
      console.error("PDF download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [plan.tripDestinations, downloading]);

  return (
    <div ref={summaryRef} className="space-y-4">
      {/* ── Quick Actions ───────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>Quick Actions</p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className={`gap-2 ${
              isDarkMode
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
            disabled={downloading}
            onClick={handleDownloadPdf}
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            {downloading ? "Downloading..." : "Download Summary"}
          </Button>
          <StatusBadge status={plan.status} config={PLAN_STATUS_CONFIG} />
          <span
            className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border ${
              isDarkMode
                ? "border-gray-600 bg-gray-700/40 text-gray-300"
                : "border-gray-300 bg-gray-50 text-gray-600"
            }`}
          >
            <Pill className="w-3.5 h-3.5" />
            {completedProducts}/{totalProducts} products complete
          </span>
        </div>
      </div>

      {/* ── Trip Details ────────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>
          <Plane className="w-4 h-4 text-blue-500" />
          Trip Details
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Row
            label="Destinations"
            value={plan.tripDestinations.join(", ") || "Not specified"}
          />
          <Row label="Departure" value={formatDate(plan.departureDate)} />
          <Row label="Return" value={formatDate(plan.returnDate)} />
          <Row
            label="Trip Types"
            value={
              plan.tripTypes.length > 0
                ? plan.tripTypes
                    .map((t) => TRIP_TYPE_LABELS[t] || t)
                    .join(", ")
                : "Not specified"
            }
          />
        </div>
        {plan.activities.length > 0 && (
          <>
            <div className={divider} />
            <Row
              label="Activities"
              value={
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {plan.activities.map((a) => (
                    <span
                      key={a}
                      className={`inline-flex text-xs px-2 py-0.5 rounded-full border ${
                        isDarkMode
                          ? "border-gray-600 bg-gray-700/40 text-gray-300"
                          : "border-gray-200 bg-gray-50 text-gray-600"
                      }`}
                    >
                      {ACTIVITY_LABELS[a] || a}
                    </span>
                  ))}
                </div>
              }
            />
          </>
        )}
        {plan.tripNotes && (
          <>
            <div className={divider} />
            <Row label="Trip Notes" value={plan.tripNotes} />
          </>
        )}
      </div>

      {/* ── NaTHNaC Assessment ──────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>
          <Globe className="w-4 h-4 text-teal-500" />
          NaTHNaC Assessment
        </p>
        <div className="space-y-3">
          <Row
            label="NaTHNaC consulted"
            value={plan.nathnacConsulted ? "Yes" : "No"}
          />
          {plan.nathnacConsulted && (
            <>
              <div className={divider} />
              <Row
                label="Destinations assessed"
                value={plan.nathnacDestinationsAssessed || "Not specified"}
              />
              <div className={divider} />
              <Row
                label="Vaccines recommended"
                value={plan.nathnacVaccinesRecommended || "None"}
              />
              <div className={divider} />
              <Row
                label="Malaria risk / resistance agent"
                value={plan.nathnacMalariaRiskResistanceAgent || "None"}
              />
              <div className={divider} />
              <Row
                label="Entry requirements"
                value={plan.nathnacEntryRequirements || "None"}
              />
              <div className={divider} />
              <Row
                label="Other medicines indicated"
                value={plan.nathnacOtherMedicinesIndicated || "None"}
              />
            </>
          )}
          {plan.dengueSeropositiveConfirmed && (
            <>
              <div className={divider} />
              <Row
                label="Dengue seropositive confirmed"
                value={
                  plan.dengueEvidenceDescription
                    ? `Yes — ${plan.dengueEvidenceDescription}`
                    : "Yes"
                }
              />
            </>
          )}
        </div>
      </div>

      {/* ── Product Basket ──────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>
          <Pill className="w-4 h-4 text-indigo-500" />
          Product Basket ({totalProducts} product{totalProducts !== 1 ? "s" : ""})
        </p>
        {plan.basket.length === 0 ? (
          <p
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            No products in basket
          </p>
        ) : (
          <div className="space-y-2">
            {plan.basket.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                isDarkMode={isDarkMode}
                divider={divider}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Co-Admin Constraints ────────────────────────────────────── */}
      {plan.coAdminMatrixOutput.length > 0 && (
        <div className={sectionCard}>
          <p className={sectionHeading}>
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Co-Administration Constraints ({plan.coAdminMatrixOutput.length})
          </p>
          <div className="space-y-2">
            {plan.coAdminMatrixOutput.map((constraint, idx) => (
              <ConstraintCard
                key={idx}
                constraint={constraint}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Safety Flags ────────────────────────────────────────────── */}
      {plan.safetyFlagAcknowledgements.length > 0 && (
        <div className={sectionCard}>
          <p className={sectionHeading}>
            <ShieldAlert className="w-4 h-4 text-red-500" />
            Safety Flags ({plan.safetyFlagAcknowledgements.length})
          </p>
          <div className="space-y-2">
            {plan.safetyFlagAcknowledgements.map((ack) => (
              <div
                key={ack.flagKey}
                className={`rounded-lg border p-2.5 space-y-1 ${
                  isDarkMode
                    ? "border-red-800/50 bg-red-900/10"
                    : "border-red-200 bg-red-50"
                }`}
              >
                <p
                  className={`text-sm font-medium ${
                    isDarkMode ? "text-red-300" : "text-red-800"
                  }`}
                >
                  {SAFETY_FLAG_LABELS[ack.flagKey] || ack.flagKey}
                </p>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-red-400/70" : "text-red-600"
                  }`}
                >
                  Acknowledged {formatDateTime(ack.acknowledgedAt)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Visit History ───────────────────────────────────────────── */}
      {planVisits.length > 0 && (
        <div className={sectionCard}>
          <p className={sectionHeading}>
            <Calendar className="w-4 h-4 text-green-500" />
            Visit History ({planVisits.length} visit
            {planVisits.length !== 1 ? "s" : ""})
          </p>
          <div className="space-y-2">
            {planVisits.map((visit) => (
              <div
                key={visit.id}
                className={`rounded-lg border p-2.5 space-y-1 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700/40"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {formatDate(visit.date)}
                  </p>
                  <span
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {visit.productsActioned.length} product
                    {visit.productsActioned.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Pharmacist: {visit.pharmacistName}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Dose Records ────────────────────────────────────────────── */}
      {planAuditRecords.length > 0 && (
        <div className={sectionCard}>
          <p className={sectionHeading}>
            <Syringe className="w-4 h-4 text-violet-500" />
            Dose Records ({planAuditRecords.length})
          </p>
          <div className="space-y-2">
            {planAuditRecords.map((record) => (
              <DoseRecordCard
                key={record.auditRecordId}
                record={record}
                productName={
                  plan.basket.find((p) => p.id === record.productId)
                    ?.productName || "Unknown"
                }
                isDarkMode={isDarkMode}
                divider={divider}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Plan Info ───────────────────────────────────────────────── */}
      <div className={sectionCard}>
        <p className={sectionHeading}>
          <ClipboardList className="w-4 h-4 text-gray-500" />
          Plan Information
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Created by" value={plan.createdByName || "Not recorded"} />
          <Row label="Created" value={formatDateTime(plan.createdAt)} />
          <Row label="Status" value={<StatusBadge status={plan.status} config={PLAN_STATUS_CONFIG} />} />
          {plan.bookingId && <Row label="Booking ID" value={plan.bookingId} />}
        </div>
        {plan.scheduleOverrideReason && (
          <>
            <div className={divider} />
            <Row
              label="Schedule override reason"
              value={plan.scheduleOverrideReason}
            />
          </>
        )}
        {plan.menACWYEntitlementAcknowledged && (
          <>
            <div className={divider} />
            <Row
              label="MenACWY entitlement"
              value="Patient acknowledged as eligible (under 25)"
            />
          </>
        )}
      </div>
    </div>
  );
}

function ProductCard({
  product,
  isDarkMode,
  divider,
}: {
  product: TravelHealthPlanProduct;
  isDarkMode: boolean;
  divider: string;
}) {
  const completedDoses = product.doseSchedule.filter(
    (d) => d.administeredDate,
  ).length;
  const totalDoses = product.doseSchedule.length;

  return (
    <div
      className={`rounded-lg border p-3 space-y-2 ${
        isDarkMode
          ? "border-gray-600 bg-gray-700/40"
          : "border-gray-200 bg-gray-50"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p
            className={`text-sm font-medium ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {product.productName}
          </p>
          {product.selectedBrand && (
            <p
              className={`text-xs ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Brand: {product.selectedBrand}
            </p>
          )}
        </div>
        <span
          className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
            isDarkMode
              ? (PRODUCT_STATUS_CONFIG[product.status]?.dark ||
                "bg-gray-900/20 border-gray-700 text-gray-300")
              : (PRODUCT_STATUS_CONFIG[product.status]?.light ||
                "bg-gray-50 border-gray-300 text-gray-700")
          }`}
        >
          {PRODUCT_STATUS_CONFIG[product.status]?.label || product.status}
        </span>
      </div>

      {totalDoses > 0 && (
        <>
          <div className={divider} />
          <div className="flex items-center gap-2">
            <div className={`flex-1 h-1.5 rounded-full ${isDarkMode ? "bg-gray-600" : "bg-gray-200"} overflow-hidden`}>
              <div
                className="h-full rounded-full bg-green-500 transition-all"
                style={{
                  width: `${totalDoses > 0 ? (completedDoses / totalDoses) * 100 : 0}%`,
                }}
              />
            </div>
            <span
              className={`text-xs whitespace-nowrap ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {completedDoses}/{totalDoses} doses
            </span>
          </div>
        </>
      )}

      {product.offLabel && (
        <p
          className={`text-xs ${
            isDarkMode ? "text-amber-400" : "text-amber-600"
          }`}
        >
          Off-label{product.offLabelRationale ? `: ${product.offLabelRationale}` : ""}
        </p>
      )}
      {product.blackTriangle && (
        <p
          className={`text-xs ${
            isDarkMode ? "text-amber-400" : "text-amber-600"
          }`}
        >
          ▼ Black triangle product
        </p>
      )}
    </div>
  );
}

function ConstraintCard({
  constraint,
  isDarkMode,
}: {
  constraint: CoAdminConstraint;
  isDarkMode: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-2.5 space-y-1 ${
        isDarkMode
          ? "border-amber-800/50 bg-amber-900/10"
          : "border-amber-200 bg-amber-50"
      }`}
    >
      <p
        className={`text-sm font-medium ${
          isDarkMode ? "text-amber-300" : "text-amber-800"
        }`}
      >
        {constraint.productNameA} ↔ {constraint.productNameB}
      </p>
      <p
        className={`text-xs ${
          isDarkMode ? "text-amber-400/70" : "text-amber-700"
        }`}
      >
        {constraint.rule}
      </p>
      {constraint.schedulingRecommendation && (
        <p
          className={`text-xs italic ${
            isDarkMode ? "text-amber-400/50" : "text-amber-600"
          }`}
        >
          {constraint.schedulingRecommendation}
        </p>
      )}
    </div>
  );
}

function DoseRecordCard({
  record,
  productName,
  isDarkMode,
  divider,
}: {
  record: TravelHealthDoseAuditRecord;
  productName: string;
  isDarkMode: boolean;
  divider: string;
}) {
  return (
    <div
      className={`rounded-lg border p-2.5 space-y-2 ${
        isDarkMode
          ? "border-gray-600 bg-gray-700/40"
          : "border-gray-200 bg-gray-50"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p
            className={`text-sm font-medium ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {productName} — Dose {record.doseNumber}
          </p>
          <p
            className={`text-xs ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {record.brandName} · {record.serviceType === "administration" ? "Administered" : "Supplied"}
          </p>
        </div>
        <span
          className={`text-xs ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          {formatDateTime(record.administeredAt)}
        </span>
      </div>

      <div className={divider} />

      <div
        className={`grid grid-cols-2 gap-x-4 gap-y-1 text-xs ${
          isDarkMode ? "text-gray-300" : "text-gray-600"
        }`}
      >
        <span>Batch: {record.batchNumber}</span>
        <span>Expiry: {formatDate(record.expiryDate)}</span>
        {record.route && <span>Route: {record.route}</span>}
        {record.injectionSite && <span>Site: {record.injectionSite}</span>}
        {record.observationMinutes != null && (
          <span>Observation: {record.observationMinutes} min</span>
        )}
      </div>

      {record.adverseReaction.occurred && (
        <>
          <div className={divider} />
          <div
            className={`rounded p-2 ${
              isDarkMode
                ? "bg-red-900/20 border border-red-800/50"
                : "bg-red-50 border border-red-200"
            }`}
          >
            <p
              className={`text-xs font-medium ${
                isDarkMode ? "text-red-300" : "text-red-800"
              }`}
            >
              Adverse reaction recorded
            </p>
            {record.adverseReaction.details && (
              <p
                className={`text-xs mt-0.5 ${
                  isDarkMode ? "text-red-400/70" : "text-red-600"
                }`}
              >
                {record.adverseReaction.details}
              </p>
            )}
            {record.adverseReaction.yellowCardSubmitted && (
              <p
                className={`text-xs mt-0.5 ${
                  isDarkMode ? "text-red-400/70" : "text-red-600"
                }`}
              >
                Yellow Card submitted
              </p>
            )}
          </div>
        </>
      )}

      {record.gpNotificationNotes && (
        <>
          <div className={divider} />
          <p
            className={`text-xs italic ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            GP notes: {record.gpNotificationNotes}
          </p>
        </>
      )}
    </div>
  );
}
